import React, { useState, Children } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboardIcon, UsersIcon, UserCheckIcon, ShipIcon, FileTextIcon, CreditCardIcon, AlertTriangleIcon, BarChart3Icon, SmartphoneIcon, GitBranchIcon, ShieldCheckIcon, BuildingIcon, CompassIcon, LeafIcon, ChevronDownIcon, SettingsIcon, AnchorIcon, FileCheckIcon } from 'lucide-react';
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
}
interface NavGroup {
  title: string;
  items: NavItem[];
}
export function Sidebar() {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>(['/tour']);
  const toggleExpand = (path: string) => {
    setExpandedItems(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };
  const navGroups: NavGroup[] = [{
    title: 'OVERVIEW',
    items: [{
      label: 'Dashboard',
      path: '/',
      icon: <LayoutDashboardIcon size={20} data-id="element-112" />
    }]
  }, {
    title: 'MANAGEMENT',
    items: [{
      label: 'User Management',
      path: '/users',
      icon: <UsersIcon size={20} data-id="element-113" />
    }, {
      label: 'Role & Permission',
      path: '/roles',
      icon: <ShieldCheckIcon size={20} data-id="element-114" />
    }, {
      label: 'Beneficiary Management',
      path: '/beneficiaries',
      icon: <UserCheckIcon size={20} data-id="element-115" />
    }, {
      label: 'Organization Management',
      path: '/organizations',
      icon: <BuildingIcon size={20} data-id="element-116" />
    }, {
      label: 'Vessel Management',
      path: '/vessels',
      icon: <ShipIcon size={20} data-id="element-117" />
    }, {
      label: 'BLC Management',
      path: '/blc',
      icon: <AnchorIcon size={20} data-id="element-118" />
    }]
  }, {
    title: 'OPERATIONS',
    items: [{
      label: 'Conservation Management',
      path: '/conservation',
      icon: <LeafIcon size={20} data-id="element-119" />
    }, {
      label: 'Harvest Management',
      path: '/harvest',
      icon: <FileTextIcon size={20} data-id="element-120" />
    }, {
      label: 'Tour Management',
      path: '/tour',
      icon: <CompassIcon size={20} data-id="element-121" />,
      children: [{
        label: 'Guide Management',
        path: '/guides',
        icon: <CompassIcon size={18} data-id="element-122" />
      }]
    }, {
      label: 'Smart ID & RFID',
      path: '/smart-id',
      icon: <CreditCardIcon size={20} data-id="element-123" />
    }]
  }, {
    title: 'COMPLIANCE',
    items: [{
      label: 'Forest Case & Compliance',
      path: '/compliance',
      icon: <AlertTriangleIcon size={20} data-id="element-124" />
    }]
  }, {
    title: 'ANALYTICS',
    items: [{
      label: 'Reports & Analytics',
      path: '/reports',
      icon: <BarChart3Icon size={20} data-id="element-125" />
    }]
  }, {
    title: 'FIELD',
    items: [{
      label: 'Mobile Field Officer',
      path: '/mobile',
      icon: <SmartphoneIcon size={20} data-id="element-126" />
    }]
  }, {
    title: 'REFERENCE',
    items: [{
      label: 'User Flow',
      path: '/user-flow',
      icon: <GitBranchIcon size={20} data-id="element-127" />
    }]
  }, {
    title: 'SETTINGS',
    items: [{
      label: 'System Configuration',
      path: '/config',
      icon: <SettingsIcon size={20} data-id="element-128" />
    }]
  }];
  return <div className="w-64 h-screen bg-white border-r border-gray-200 flex flex-col fixed left-0 top-0" data-id="element-129">
      {/* Logo Area */}
      <div className="p-6 border-b border-gray-200" data-id="element-130">
        <div className="flex items-center gap-3" data-id="element-131">
          <div className="w-10 h-10 bg-[#1B5E20] rounded-lg flex items-center justify-center text-white font-bold" data-id="element-132">
            PMS
          </div>
          <div data-id="element-133">
            <div className="font-bold text-sm text-gray-900" data-id="element-134">
              Bangladesh Forest
            </div>
            <div className="text-xs text-gray-600" data-id="element-135">Department</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4" data-id="element-136">
        {navGroups.map((group, idx) => <div key={idx} className="mb-6" data-id="element-137">
            <div className="px-6 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-138">
              {group.title}
            </div>
            <div className="space-y-1" data-id="element-139">
              {group.items.map(item => {
            const isActive = location.pathname === item.path;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedItems.includes(item.path);
            const isChildActive = hasChildren && item.children!.some(c => location.pathname === c.path);
            return <div key={item.path} data-id="element-140">
                    <div className="flex items-center" data-id="element-141">
                      <Link to={item.path} className={`flex items-center gap-3 px-6 py-2.5 text-sm transition-colors flex-1 ${isActive || isChildActive ? 'bg-[#1B5E20] text-white font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-142">
                        {item.icon}
                        <span data-id="element-143">{item.label}</span>
                      </Link>
                      {hasChildren && <button onClick={() => toggleExpand(item.path)} className={`pr-4 py-2.5 transition-colors ${isActive || isChildActive ? 'text-white' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-144">
                          <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-145" />
                        </button>}
                    </div>
                    {hasChildren && isExpanded && <div className="ml-4 border-l border-gray-200" data-id="element-146">
                        {item.children!.map(child => {
                  const isChildItemActive = location.pathname === child.path;
                  return <Link key={child.path} to={child.path} className={`flex items-center gap-3 pl-8 pr-6 py-2 text-sm transition-colors ${isChildItemActive ? 'bg-[#E8F5E9] text-[#1B5E20] font-medium' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-147">
                              {child.icon}
                              <span data-id="element-148">{child.label}</span>
                            </Link>;
                })}
                      </div>}
                  </div>;
          })}
            </div>
          </div>)}
      </div>

      {/* User Info */}
      <div className="p-6 border-t border-gray-200" data-id="element-149">
        <div className="flex items-center gap-3" data-id="element-150">
          <div className="w-10 h-10 bg-[#2E7D32] rounded-full flex items-center justify-center text-white font-semibold" data-id="element-151">
            A
          </div>
          <div data-id="element-152">
            <div className="text-sm font-medium text-gray-900" data-id="element-153">Admin</div>
            <div className="text-xs text-gray-600" data-id="element-154">HQ Level</div>
          </div>
        </div>
      </div>
    </div>;
}